import { LoginForm } from '@pwa/components/auth/login-form';
import { SignupForm } from '@pwa/components/auth/signup-form';
import { cn } from '@repo/ui/lib/utils';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { z } from 'zod';

const searchSchema = z
	.object({
		redirect: z.string().optional(),
		error: z.string().optional(),
		mode: z.enum(['login', 'signup']).optional().default('login'),
	})
	.catchall(z.unknown());

export const Route = createFileRoute('/(auth)/login')({
	validateSearch: searchSchema,
	component: RouteComponent,
});

function RouteComponent() {
	const { redirect, error, mode } = Route.useSearch();
	const navigate = useNavigate();

	const isSignup = mode === 'signup';

	useEffect(() => {
		document.title = isSignup ? 'Create account · Scout' : 'Sign in · Scout';
	}, [isSignup]);

	const setMode = (next: 'login' | 'signup') => {
		void navigate({
			to: '/login',
			search: (prev) => ({ ...prev, mode: next }),
			replace: true,
		});
	};

	return (
		<div className='flex w-full flex-col gap-5'>
			<div
				role='tablist'
				aria-label='Authentication mode'
				className='relative isolate grid grid-cols-2 rounded-lg border border-border bg-muted/40 p-0.5 text-sm font-medium'
			>
				<span
					aria-hidden='true'
					className={cn(
						'pointer-events-none absolute top-0.5 bottom-0.5 left-0.5 w-[calc(50%-2px)] rounded-md bg-background shadow-sm ring-1 ring-foreground/5 transition-transform duration-300 ease-out',
						isSignup ? 'translate-x-full' : 'translate-x-0',
					)}
				/>
				<button
					type='button'
					role='tab'
					aria-selected={!isSignup}
					tabIndex={isSignup ? -1 : 0}
					onClick={() => setMode('login')}
					className='relative z-10 rounded-md py-1.5 text-foreground/70 transition-colors duration-200 aria-selected:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40'
				>
					Sign in
				</button>
				<button
					type='button'
					role='tab'
					aria-selected={isSignup}
					tabIndex={isSignup ? 0 : -1}
					onClick={() => setMode('signup')}
					className='relative z-10 rounded-md py-1.5 text-foreground/70 transition-colors duration-200 aria-selected:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40'
				>
					Create account
				</button>
			</div>

			<div className='relative'>
				<div key={mode} className='animate-in fade-in slide-in-from-bottom-1 duration-[240ms] ease-out fill-mode-both'>
					{isSignup ? (
						<SignupForm redirect={redirect} error={error} />
					) : (
						<LoginForm redirect={redirect} error={error} />
					)}
				</div>
			</div>
		</div>
	);
}
